import { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import { CheckboxRadioAlign, Link, LinkVariant, RadioButton } from '@jsmdg/yoshi';
import { isBox } from '../../../shared/product-safeguards';
import { type Product } from '../../../shared/types';
import { formatPrice } from '../../helper/formatPrice';
import { type UseBuyboxReturn } from '../../hooks/useBuybox';
import { ClusterType } from '../../types/enums/clusterType';
import { trackBuyVoucherOrShowClusterClicked } from './ClusterProductGroup.tracking';
import styles from './ClusterProductGroup.module.scss';

type ClusterProductGroupProps = {
    readonly product: Product;
    readonly buyBoxProps: UseBuyboxReturn;
};

const messages = defineMessages({
    minPriceText: {
        defaultMessage: 'ab',
    },

    experienceClusterBox: {
        defaultMessage: 'Einzelerlebnis auswählen',
    },
    experienceClusterBoxSubtext: {
        defaultMessage: 'Wähle ein Einzelerlebnis aus {clusterLength} Erlebnisse bis zu 199 € aus',
    },
    experienceClusterValueVoucher: {
        defaultMessage: 'Kollektions-Gutschein kaufen',
    },
    experienceClusterValueVoucherSubtext: {
        defaultMessage: 'Lass dem Beschenkten maximale Flexibilität bei der Standortwahl',
    },
});

const ClusterProductGroup = ({
    buyBoxProps,
    product,
}: ClusterProductGroupProps): JSX.Element | null => {
    const intl = useIntl();
    const [selectedType, setSelectedType] = useState<string>(ClusterType.PRODUCT_VOUCHER);
    const { handleClusterType } = buyBoxProps;
    const renderPriceDisplay = (
        value: string,
        currencyCode: string = 'EUR',
        amount: number = 0,
    ): JSX.Element => (
        <strong
            className={classNames('theme-text-subtitle-text h-100 text-nowrap', styles.resetColor)}
        >
            {ClusterType.PRODUCT_VOUCHER === value && intl.formatMessage(messages.minPriceText)}{' '}
            {formatPrice(intl.locale, amount, currencyCode)}
            &nbsp;
        </strong>
    );

    const radioOptions = [
        {
            titleMessage: messages.experienceClusterBox,
            subtextMessage: messages.experienceClusterBoxSubtext,
            price: isBox(product) ? product?.minPrice?.gross.amount : product.price.gross.amount,
            currency: isBox(product)
                ? product?.minPrice?.gross.currencyCode
                : product.price.gross.currencyCode,
            type: ClusterType.PRODUCT_VOUCHER,
        },
        {
            titleMessage: messages.experienceClusterValueVoucher,
            subtextMessage: messages.experienceClusterValueVoucherSubtext,
            price: product.price.gross.amount,
            currency: product.price.gross.currencyCode,
            type: ClusterType.FLEXIBLE_VOUCHER,
        },
    ];

    const handleTypeSelect = (value: string) => {
        handleClusterType(value as ClusterType);
        setSelectedType(value);
        const label =
            (value as ClusterType) === ClusterType.PRODUCT_VOUCHER
                ? 'product voucher'
                : 'flexible voucher';
        trackBuyVoucherOrShowClusterClicked(label);
    };

    return (
        <div
            className={classNames(
                'd-flex pt-2x flex-column px-3x px-xs-6x',
                styles.productGroupWrapper,
            )}
            data-testid="product-group-box-product"
        >
            {radioOptions.map(option => (
                <RadioButton
                    key={option.type}
                    checked={selectedType === option.type}
                    onChange={handleTypeSelect}
                    value={option.type}
                    classNames={{
                        wrapperClassName: classNames(
                            styles.productGroupItem,
                            'text-decoration-none py-2x',
                        ),
                    }}
                    disabled={false}
                    id={option.type}
                    textAlign={CheckboxRadioAlign.Top}
                >
                    <Link
                        className="d-flex align-items-center text-decoration-none justify-content-between flex-grow-1"
                        href={undefined}
                        variant={LinkVariant.Grey}
                    >
                        <div className="d-flex flex-column flex-grow-1">
                            <strong
                                className={classNames(
                                    'theme-text-subtitle-text',
                                    styles.resetColor,
                                )}
                            >
                                {intl.formatMessage(option.titleMessage)}
                            </strong>
                            <span className="fw-regular">
                                {intl.formatMessage(option.subtextMessage, {
                                    clusterLength: 1,
                                })}
                            </span>
                        </div>
                        {renderPriceDisplay(option.type, option.currency, option.price)}
                    </Link>
                </RadioButton>
            ))}
        </div>
    );
};

export { ClusterProductGroup };
